import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Users, Award, Calendar, ClipboardList, Download } from 'lucide-react';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { StatCards, UpcomingMatches, RecentRequests, UpcomingRoundRequests } from '@/components/DashboardWidgets';
import { ThinDonutCard } from '@/components/ThinDonutCard';
import { DashboardCustomizeBar, DashboardCustomizePanel, DashboardGrid } from '@/components/DashboardLayout';
import { useDashboardCustomization } from '@/hooks/useDashboardCustomization';
import { WIDGET_PERM } from '@/lib/dashboardPerms';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { SystemOverviewCard } from '@/components/SystemOverviewWidgets';
import { toast } from 'sonner';

const STATUS_BAR_COLORS = {
  submitted: '#3B82F6', under_review: '#F3CF5B', approved: '#10B981',
  partially_approved: '#7EC9C9', rejected: '#E44E50', cancelled: '#6B7280',
  cancellation_requested: '#F59E0B', pending_sponsor_approval: '#6763A9', delivered: '#7EC9C9',
};

const defaultVis = { pieChart: true, systemOverview: true, upcomingMatches: true, upcomingRoundRequests: true, recentRequests: true };
const defaultOrder = ['pieChart', 'systemOverview', 'upcomingMatches', 'upcomingRoundRequests', 'recentRequests'];
const defaultSizes = { pieChart: 'small', systemOverview: 'small', upcomingMatches: 'large', upcomingRoundRequests: 'full', recentRequests: 'full' };

export default function AdminDashboard() {
  const { t } = useTranslation();
  const { user, hasPerm } = useAuth();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [upcomingRound, setUpcomingRound] = useState(null);
  const [exportConfirm, setExportConfirm] = useState(false);
  const [exporting, setExporting] = useState(false);

  const customize = useDashboardCustomization('isp_dashboard', defaultVis, defaultOrder, defaultSizes, (k) => hasPerm(WIDGET_PERM[k]));

  useEffect(() => {
    api.get('/api/dashboard/stats').then(r => setStats(r.data)).catch(() => {}).finally(() => setLoading(false));
    api.get('/api/dashboard/system-overview').then(r => setOverview(r.data)).catch(() => {});
    api.get('/api/dashboard/upcoming-round').then(r => setUpcomingRound(r.data)).catch(() => {});
  }, []);

  if (loading) return <div className="flex items-center justify-center h-64 text-gray-400">{t('loading')}</div>;

  const simpleCards = [
    { label: t('active_sponsors'), value: stats?.total_sponsors || 0, icon: Users, color: 'text-blue-600', bg: 'bg-blue-50', link: '/sponsors' },
    { label: t('active_rights'), value: stats?.total_rights || 0, icon: Award, color: 'text-violet-600', bg: 'bg-violet-50', link: '/rights' },
    { label: t('upcoming_matches'), value: stats?.upcoming_matches || 0, icon: Calendar, color: 'text-amber-600', bg: 'bg-amber-50', link: '/matches' },
  ];
  const requestCard = {
    label: t('total_requests'), value: stats?.total_requests || 0, icon: ClipboardList, color: 'text-emerald-600', bg: 'bg-emerald-50', link: '/requests',
    breakdown: [
      { label: t('pending'), value: stats?.pending_requests || 0, dotColor: 'bg-amber-500' },
      { label: t('approved'), value: stats?.approved_requests || 0, dotColor: 'bg-green-500' },
      { label: t('rejected'), value: stats?.rejected_requests || 0, dotColor: 'bg-red-500' },
    ]
  };
  const pieData = Object.entries(stats?.request_status_breakdown || {})
    .filter(([, v]) => v > 0)
    .map(([key, value]) => ({
      name: key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
      value,
      color: STATUS_BAR_COLORS[key],
    }));
  if (pieData.length === 0 && stats?.total_requests > 0) {
    if (stats.approved_requests) pieData.push({ name: t('approved'), value: stats.approved_requests, color: STATUS_BAR_COLORS.approved });
    if (stats.pending_requests) pieData.push({ name: t('pending'), value: stats.pending_requests, color: STATUS_BAR_COLORS.submitted });
    const other = (stats.total_requests || 0) - (stats.pending_requests || 0) - (stats.approved_requests || 0);
    if (other > 0) pieData.push({ name: t('other'), value: other, color: STATUS_BAR_COLORS.cancelled });
  }

  const widgetList = [
    { key: 'pieChart', label: t('requests_overview') },
    { key: 'systemOverview', label: 'System Overview' },
    { key: 'upcomingMatches', label: t('upcoming_matches') || 'Upcoming Matches' },
    { key: 'upcomingRoundRequests', label: 'Upcoming Round Requests' },
    { key: 'recentRequests', label: t('recent_requests') },
  ];
  const allowedWidgetList = widgetList.filter(w => hasPerm(WIDGET_PERM[w.key]));

  const renderWidget = (key, size) => {
    switch (key) {
      case 'pieChart': return <ThinDonutCard title={t('requests_overview')} data={pieData} />;
      case 'upcomingMatches': return <UpcomingMatches thisRoundMatches={stats?.this_round_matches} thisRound={stats?.this_round} nextRoundMatches={stats?.next_round_matches} nextRound={stats?.next_round} size={customize.widgetSizes['upcomingMatches'] || 'small'} />;
      case 'recentRequests': return <RecentRequests requests={stats?.recent_requests} size={customize.widgetSizes['recentRequests'] || 'full'} />;
      case 'upcomingRoundRequests': return <UpcomingRoundRequests data={upcomingRound} />;
      case 'systemOverview': return <SystemOverviewCard data={overview} />;
      default: return null;
    }
  };

  const exportButton = (
    <>
      <Button data-testid="export-data-btn" variant="outline" size="sm" className="text-sm h-8 border-gray-200 dark:border-gray-700" disabled={exporting} onClick={() => setExportConfirm(true)}>
        <Download size={14} className="mr-1.5" /> {exporting ? 'Exporting...' : 'Export'}
      </Button>
      <ConfirmDialog
        open={exportConfirm}
        onCancel={() => setExportConfirm(false)}
        onConfirm={async () => {
          setExportConfirm(false); setExporting(true);
          try {
            const token = localStorage.getItem('srp_token');
            const resp = await fetch(`${api.defaults.baseURL}/api/export/all`, { headers: { Authorization: `Bearer ${token}` } });
            const blob = await resp.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a'); a.href = url; a.download = 'INCEPTION_Sports_Hub_Export.xlsx'; a.click();
            URL.revokeObjectURL(url); toast.success('Export downloaded');
          } catch { toast.error('Export failed'); }
          setExporting(false);
        }}
        title="Export All Data"
        description="This will download all system data (Sponsors, Users, Rights, Matches, Requests) as an Excel file."
        confirmText="Download"
      />
    </>
  );

  return (
    <div data-testid="admin-dashboard" className="space-y-5 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="page-title">{t('welcome_name', { name: user?.name?.split(' ')[0] || '' })}</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1 text-xs lg:text-sm hidden sm:block">{t('system_overview')}</p>
        </div>
        <DashboardCustomizeBar {...customize} widgetList={allowedWidgetList} extraButtons={exportButton} />
      </div>
      <DashboardCustomizePanel {...customize} widgetList={allowedWidgetList} />
      {hasPerm(WIDGET_PERM.statCards) && <StatCards cards={simpleCards} requestCard={requestCard} />}
      <DashboardGrid {...customize} renderWidget={renderWidget} />
    </div>
  );
}
